import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api } from "../api";
import { MetricsPage } from "./MetricsPage";
import { PhpModulesPage } from "./PhpModulesPage";
import { PanelConfigPage } from "./PanelConfigPage";
import type { PanelUser } from "../types";

type ServiceLight = {
  id: string;
  label: string;
  status: "ok" | "degraded" | "down" | "unknown";
  detail: string;
};

type BasicConfig = {
  hostname: string;
  primaryIp: string;
  contactEmail: string;
  resolvers: string[];
};

type Tweaks = {
  allowEndUserBackups: boolean;
  allowEndUserCron: boolean;
  require2faStaff: boolean;
  showPhpSelector: boolean;
  maintenanceMode: boolean;
  defaultPhpVersion: string;
};

/**
 * W8 — WHM Server Status / Basic Config / Tweaks / PHP
 */
export function ServerConfigPage({
  accounts,
  currentUser,
  onRefreshAccounts,
  initialTab
}: {
  accounts: PanelUser[];
  currentUser: PanelUser;
  onRefreshAccounts: () => Promise<void>;
  initialTab?:
    | "status"
    | "basic"
    | "tweaks"
    | "php"
    | "metrics"
    | "panel";
}) {
  const [tab, setTab] = useState<
    "status" | "basic" | "tweaks" | "php" | "metrics" | "panel"
  >(initialTab || "panel");
  const [services, setServices] = useState<ServiceLight[]>([]);
  const [overall, setOverall] = useState<string>("unknown");
  const [checkedAt, setCheckedAt] = useState("");
  const [basic, setBasic] = useState<BasicConfig>({
    hostname: "",
    primaryIp: "",
    contactEmail: "",
    resolvers: []
  });
  const [resolversText, setResolversText] = useState("");
  const [tweaks, setTweaks] = useState<Tweaks | null>(null);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    setError("");
    try {
      const res = await api<{
        services: ServiceLight[];
        overall: string;
        checkedAt: string;
      }>("/api/v1/services/status");
      setServices(res.services || []);
      setOverall(res.overall || "unknown");
      setCheckedAt(res.checkedAt || "");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const loadBasic = useCallback(async () => {
    try {
      const res = await api<{ config: BasicConfig }>(
        "/api/v1/server-config/basic"
      );
      setBasic(res.config);
      setResolversText((res.config.resolvers || []).join("\n"));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const loadTweaks = useCallback(async () => {
    try {
      const res = await api<{ tweaks: Tweaks }>("/api/v1/server-config/tweaks");
      setTweaks(res.tweaks);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    if (tab === "status") void loadStatus();
    if (tab === "basic") void loadBasic();
    if (tab === "tweaks") void loadTweaks();
  }, [tab, loadStatus, loadBasic, loadTweaks]);

  useEffect(() => {
    if (initialTab) setTab(initialTab);
  }, [initialTab]);

  const saveBasic = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const res = await api<{ config: BasicConfig }>(
        "/api/v1/server-config/basic",
        {
          method: "PATCH",
          body: JSON.stringify({
            ...basic,
            resolvers: resolversText
          })
        }
      );
      setBasic(res.config);
      setResolversText((res.config.resolvers || []).join("\n"));
      setMsg("Basic configuration saved");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const saveTweaks = async (e: FormEvent) => {
    e.preventDefault();
    if (!tweaks) return;
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const res = await api<{ tweaks: Tweaks }>(
        "/api/v1/server-config/tweaks",
        {
          method: "PATCH",
          body: JSON.stringify(tweaks)
        }
      );
      setTweaks(res.tweaks);
      setMsg("Tweak settings saved");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const statusColor = (s: string) => {
    if (s === "ok") return "ok";
    if (s === "degraded") return "warn";
    if (s === "down") return "bad";
    return "";
  };

  return (
    <>
      <div className="topbar">
        <div>
          <h1>Server Configuration</h1>
          <p>
            Panel Config · service status · hostname/IP/NS · PHP/FPM · ports
          </p>
        </div>
        <div className="topbar-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              if (tab === "status") void loadStatus();
              if (tab === "basic") void loadBasic();
              if (tab === "tweaks") void loadTweaks();
              void onRefreshAccounts();
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="chip-row" style={{ marginBottom: 14 }}>
        {(
          [
            ["panel", "Panel Config"],
            ["status", "Service Status"],
            ["metrics", "Server Information"],
            ["basic", "Basic Config"],
            ["tweaks", "Tweak Settings"],
            ["php", "MultiPHP INI"]
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            className={`chip ${tab === k ? "chip-active" : ""}`}
            onClick={() => setTab(k)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "panel" ? <PanelConfigPage /> : null}

      {tab === "status" ? (
        <>
          <div className="whm-stats" style={{ marginBottom: 14 }}>
            <div className="whm-stat">
              <span className="whm-stat-label">Overall</span>
              <span className="whm-stat-value" style={{ fontSize: 20 }}>
                <span className={`badge ${statusColor(overall)}`}>
                  {overall === "ok"
                    ? "Healthy"
                    : overall === "degraded"
                      ? "Degraded"
                      : overall === "down"
                        ? "Down"
                        : "Unknown"}
                </span>
              </span>
              <span className="whm-stat-meta mono">
                {checkedAt
                  ? new Date(checkedAt).toLocaleString()
                  : "Not checked"}
              </span>
            </div>
            <div className="whm-stat">
              <span className="whm-stat-label">Online</span>
              <span className="whm-stat-value">
                {services.filter((s) => s.status === "ok").length}
              </span>
              <span className="whm-stat-meta">
                of {services.length} services
              </span>
            </div>
            <div className="whm-stat">
              <span className="whm-stat-label">Issues</span>
              <span className="whm-stat-value">
                {
                  services.filter(
                    (s) => s.status === "down" || s.status === "degraded"
                  ).length
                }
              </span>
              <span className="whm-stat-meta">Need attention</span>
            </div>
          </div>

          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Status</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {services.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <strong>{s.label}</strong>
                    </td>
                    <td>
                      <span className={`badge ${statusColor(s.status)}`}>
                        {s.status === "ok"
                          ? "Running"
                          : s.status === "down"
                            ? "Down"
                            : s.status === "degraded"
                              ? "Degraded"
                              : "Unknown"}
                      </span>
                    </td>
                    <td className="mono faint">{s.detail}</td>
                  </tr>
                ))}
                {services.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="muted">
                      Loading service status…
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      {tab === "basic" ? (
        <form className="panel-form" onSubmit={(e) => void saveBasic(e)}>
          <div className="panel-form-title">Basic Config</div>
          <p className="card-meta" style={{ marginBottom: 12 }}>
            Stored in the panel database for display and ops. (Hostname is not
            rewritten on the OS automatically.)
          </p>
          <div className="form-grid">
            <div className="field">
              <label>Hostname</label>
              <input
                className="mono"
                value={basic.hostname}
                onChange={(e) =>
                  setBasic((b) => ({ ...b, hostname: e.target.value }))
                }
                placeholder="server.example.com"
              />
            </div>
            <div className="field">
              <label>Primary IP</label>
              <input
                className="mono"
                value={basic.primaryIp}
                onChange={(e) =>
                  setBasic((b) => ({ ...b, primaryIp: e.target.value }))
                }
                placeholder="203.0.113.10"
              />
            </div>
            <div className="field">
              <label>Contact email</label>
              <input
                type="email"
                value={basic.contactEmail}
                onChange={(e) =>
                  setBasic((b) => ({ ...b, contactEmail: e.target.value }))
                }
              />
            </div>
            <div className="field" style={{ gridColumn: "1 / -1" }}>
              <label>Resolvers (one per line)</label>
              <textarea
                className="textarea mono"
                rows={3}
                value={resolversText}
                onChange={(e) => setResolversText(e.target.value)}
              />
            </div>
          </div>
          <button
            type="submit"
            className="btn btn-primary btn-inline"
            disabled={busy}
          >
            {busy ? "Saving…" : "Save basic config"}
          </button>
        </form>
      ) : null}

      {tab === "tweaks" && tweaks ? (
        <form className="panel-form" onSubmit={(e) => void saveTweaks(e)}>
          <div className="panel-form-title">Tweak Settings</div>
          <p className="card-meta" style={{ marginBottom: 12 }}>
            Feature flags for the control panel (stored server-side).
          </p>
          <div className="feature-check-grid">
            {(
              [
                ["allowEndUserBackups", "Allow end-user backups"],
                ["allowEndUserCron", "Allow end-user cron jobs"],
                ["require2faStaff", "Require 2FA for staff (policy flag)"],
                ["showPhpSelector", "Show MultiPHP / PHP selector"],
                ["maintenanceMode", "Maintenance mode banner"]
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="feature-check">
                <input
                  type="checkbox"
                  checked={Boolean(tweaks[key])}
                  onChange={(e) =>
                    setTweaks((t) =>
                      t ? { ...t, [key]: e.target.checked } : t
                    )
                  }
                />
                <span>
                  <strong>{label}</strong>
                </span>
              </label>
            ))}
          </div>
          <div className="field" style={{ marginTop: 14, maxWidth: 240 }}>
            <label>Default PHP version</label>
            <select
              value={tweaks.defaultPhpVersion}
              onChange={(e) =>
                setTweaks((t) =>
                  t ? { ...t, defaultPhpVersion: e.target.value } : t
                )
              }
            >
              {["8.3", "8.2", "8.1", "8.0", "7.4"].map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className="btn btn-primary btn-inline"
            disabled={busy}
            style={{ marginTop: 12 }}
          >
            {busy ? "Saving…" : "Save tweaks"}
          </button>
        </form>
      ) : null}

      {tab === "php" ? <PhpModulesPage /> : null}

      {tab === "metrics" ? (
        <MetricsPage
          accounts={accounts}
          currentUser={currentUser}
          onRefreshAccounts={onRefreshAccounts}
        />
      ) : null}
    </>
  );
}
